import { useSFY } from 'providers';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

// Todo: Switch to use 'ringSelection?.model' as 'pId' parameter in iframe URL. This is currently set to 'N4751600' as it is the only supported model in the module so far. See 'src' property value on 'StyledIframe' component.

interface IModelVisualizer {
  variant: 'EMBOSSING' | 'ENGRAVING';
  distanceRatio?: number;
  moveIcon?: boolean;
  iframeWrapper: React.ElementType;
  imageWrapper: React.ElementType;
}

const IFRAME_URL = process.env.REACT_APP_3DCONFIGURATOR_URL;

const IFRAME_FONTS = {
  EMBOSSING: {
    block: 'SACKERS',
    cursive: 'CELESTE',
  },
  ENGRAVING: {
    block: 'AVANT',
    cursive: 'SCRIPT412',
  },
};

const ModelVisualizer: React.FC<IModelVisualizer> = ({
  variant,
  distanceRatio,
  moveIcon,
  iframeWrapper: IframeWrapper,
  imageWrapper: ImageWrapper,
}) => {
  const { ringSelection, configuration } = useSFY();
  const iframeRef = useRef<any>();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [inputValues, setInputValues] = useState<EngravingConfig>();
  const [hasError, setHasError] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const healthTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const iframeLoadedRef = useRef(false);

  const getSymbol = useCallback(() => {
    const supportedSymbols = ['Heart', 'Star', 'Infinity', '.', '&'];
    const symbol = inputValues?.symbol && supportedSymbols.includes(inputValues.symbol) ? inputValues?.symbol : '';

    // * Format the symbol code differently for Engraving
    if (variant === 'ENGRAVING' && symbol.length) {
      return `symbol:'${symbol}'`;
    }

    return symbol;
  }, [variant, inputValues?.symbol]);

  /**
   * * Post message input to iframe
   * Message consists of 3 values. 'message', 'symbol', and 'messageAfterSymbol'.
   */
  const setMessage = useCallback(() => {
    const target = `${variant.toLowerCase()}Msg_1`;
    const message = inputValues?.message;
    const messageAfterSymbol = inputValues?.messageAfterSymbol;
    const symbol = getSymbol();

    const compoundMessage = `${message || ' '}${symbol || ' '}${messageAfterSymbol || ' '}`;
    iframeRef.current.contentWindow.postMessage(`{"${target}" : "${compoundMessage}"}`, '*');
  }, [inputValues, getSymbol, variant]);

  /**
   * * Post font selection to iframe
   */
  const setFont = useCallback(() => {
    const target = `${variant.toLowerCase()}Font`;
    const fontFamily = inputValues?.font === 'Cursive' ? IFRAME_FONTS[variant].cursive : IFRAME_FONTS[variant].block;

    iframeRef.current.contentWindow.postMessage(`{"${target}" : "${fontFamily}"}`, '*');
  }, [inputValues?.font, variant]);

  /**
   * * Post angle setting to iframe.
   * Can be either 'EMBOSSING' or 'ENGRAVING'.
   */
  const setAngle = useCallback(() => {
    iframeRef.current.contentWindow.postMessage(`{"${variant.toLowerCase()}DistanceRatio" : "0"}`, '*');
    iframeRef.current.contentWindow.postMessage(`{"view" : "${variant}"}`, '*');
  }, [variant]);

  /**
   * * Act on iframe state message
   * On state 'SUCCESS', post font and message input values to iframe to set the initial state.
   * The initial state can either be empty or populated, since the user can go back and edit their personalisation config.
   */
  const onMessageReceivedFromIframe = useCallback(
    (event: any) => {
      const iframe = iframeRef.current?.contentWindow;

      if (iframe !== event.source) {
        setHasError(true);
        return;
      }

      const iframeState = JSON.parse(event.data).iframeStates;

      // Clear and restart health timeout on any message to detect inactivity
      if (healthTimeoutRef.current) {
        clearTimeout(healthTimeoutRef.current);
      }
      healthTimeoutRef.current = setTimeout(() => {
        console.log('Iframe inactivity detected, falling back to image');
        setHasError(true);
      }, 30000); // 30 seconds of no messages

      console.log('iframeState received from iframe:', iframeState);

      try {
        if (iframeState === 'SUCCESS' || iframeState === 'FINISH') {
          // Clear initial timeout since SUCCESS received
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

          setIframeLoaded(true);
          iframeLoadedRef.current = true;
          setFont();
          setMessage();
          setHasError(false);

          if (iframeState === 'SKU_NOT_AVAILABLE') {
            setHasError(true);
          }

          // // * Render the Box instead of the Product for Embossing
          // if (variant && variant === 'EMBOSSING') setAngle();
        } else if (iframeState === 'ERROR' || iframeState === 'SKU_NOT_AVAILABLE') {
          setHasError(true);
          iframeLoadedRef.current = false;
          setIframeLoaded(false);
        } else {
          // For unknown states, only set error if iframe hasn't loaded yet
          console.log('Unknown iframe state:', iframeState);
          if (!iframeLoadedRef.current) {
            setHasError(true);
          }
        }
      } catch (error) {
        console.log('Error parsing iframe message:', error);
        // Only set error on parse failure if iframe hasn't loaded yet
        if (!iframeLoadedRef.current) {
          setHasError(true);
        }
      }
    },
    [setFont, setMessage],
  );

  /**
   * * Iframe listener and timeout
   * Listen to iframe state messages and set timeout for initial message
   */
  useEffect(() => {
    // Set timeout to expect initial message
    timeoutRef.current = setTimeout(() => {
      console.log('No initial message received, checking if iframe is already loaded');
      if (!iframeLoadedRef.current) {
        console.log('Iframe not loaded, falling back to image');
        setHasError(true);
      } else {
        console.log('Iframe already marked as loaded, no action needed');
      }
    }, 2500); // 2.5 seconds to allow for iframe to send message

    window.addEventListener('message', onMessageReceivedFromIframe);
    return () => {
      window.removeEventListener('message', onMessageReceivedFromIframe);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (healthTimeoutRef.current) clearTimeout(healthTimeoutRef.current);
    };
  }, [onMessageReceivedFromIframe]);

  useEffect(() => {
    if (variant && variant === 'ENGRAVING') setInputValues(ringSelection?.engraving);
    if (variant && variant === 'EMBOSSING') setInputValues(ringSelection?.embossing);
  }, [variant, ringSelection?.engraving, ringSelection?.embossing]);

  /**
   * * Change handler
   * Post input values to iframe on input change
   */
  useEffect(() => {
    if (iframeLoaded && inputValues) {
      setMessage();
      setFont();
      setAngle();
    }
  }, [inputValues, setAngle, setMessage, setFont, iframeLoaded]);

  return configuration?.model ? (
    <>
      {!hasError ? (
        <IframeWrapper>
          {!iframeLoaded && <StyledLoader>Loading...</StyledLoader>}
          <StyledIframe
            show={iframeLoaded}
            ref={iframeRef}
            src={`${IFRAME_URL}?pId=${variant === 'EMBOSSING' ? 'Box' : configuration?.model.id}&background=F6F6F6${
              distanceRatio ? '&distanceRatio=' + distanceRatio : ''
            }${moveIcon ? '&disclaimer=TRUE' : ''}`}
            style={!iframeLoaded ? { height: '0px', minHeight: '0px' } : {}}
          />
        </IframeWrapper>
      ) : (
        <ImageWrapper>
          <img src={configuration?.model.mainImageUrl} alt={configuration?.model.name || ''} />
        </ImageWrapper>
      )}
    </>
  ) : null;
};

const StyledIframe = styled.iframe<{ show?: boolean }>`
  width: 100%;
  min-height: 30vh;
  height: 100%;
  opacity: 0;
  transition: opacity 0.8s ease;
  transition-delay: 0.3s;

  @media (min-width: ${({ theme }) => theme.breakpoints.m}) {
    min-height: 50vh;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.l}) {
    min-height: 65vh;
  }

  ${({ show }) =>
    show &&
    css`
      opacity: 1;
    `}
`;

const StyledLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20vh;
  font-size: 1.2rem;
  color: #666;

  &::before {
    content: '';
    width: 20px;
    height: 20px;
    border: 2px solid #ccc;
    border-top: 2px solid #000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 10px;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.m}) {
    height: 60vh;
  }
`;

export default ModelVisualizer;
